import React, { useState } from "react";
import { motion } from "framer-motion";
import { NavLink, useNavigate } from "react-router-dom";
import "./singin.css";
import Header from "../header/header";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE
  ? `${process.env.REACT_APP_API_BASE}/admin/register`
  : "https://api-store.crescentworld.com/api/admin/register";

const Signin = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    mobile: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!formData.fname.trim()) newErrors.fname = "First name required";

    if (!formData.lname.trim()) newErrors.lname = "Last name required";

    if (!formData.email.trim()) newErrors.email = "Email required";

    if (!formData.mobile.trim()) newErrors.mobile = "Mobile required";

    if (!formData.password) newErrors.password = "Password required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    setServerError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setServerError("");

    try {
      await axios.post(API_URL, formData);

      alert("Admin Registered Successfully ✅");
      navigate("/admin");
    } catch (err) {
      setServerError(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-signup-page">
      <Header />

      <div className="signup-background">
        <div className="signup-decoration signup-decoration-one" />
        <div className="signup-decoration signup-decoration-two" />

        <motion.div
          className="signup-main-card"
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          {/* LEFT PANEL */}
          <div className="signup-info-panel">
            <div className="signup-brand">
              {/* <div className="signup-brand-icon">
                <i className="bi bi-stars" />
              </div> */}
              <span>CRESCENT WORLD</span>
            </div>

            <div className="signup-info-content">
              <span className="signup-eyebrow">ADMINISTRATION PORTAL</span>

              <h1>
                Manage your
                <br />
                business with
                <br />
                <span>confidence.</span>
              </h1>

              <p>
                Create your administrator account and get access to your
                Crescent World management dashboard.
              </p>

              <div className="signup-benefits">
                <div className="signup-benefit">
                  <span>
                    <i className="bi bi-shield-check" />
                  </span>
                  <div>
                    <h6>Secure Access</h6>
                    <p>Protected administrator access.</p>
                  </div>
                </div>

                <div className="signup-benefit">
                  <span>
                    <i className="bi bi-speedometer2" />
                  </span>
                  <div>
                    <h6>Smart Management</h6>
                    <p>Manage your business efficiently.</p>
                  </div>
                </div>

                <div className="signup-benefit">
                  <span>
                    <i className="bi bi-lightning-charge" />
                  </span>
                  <div>
                    <h6>Simple Experience</h6>
                    <p>Everything organized in one place.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="signup-panel-footer">
              © {new Date().getFullYear()} Crescent World. All rights reserved.
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="signup-form-panel">
            <motion.div
              className="signup-form-content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="signup-mobile-brand">
                <span className="signup-mobile-icon">
                  <i className="bi bi-stars" />
                </span>
                <span>CRESCENT WORLD</span>
              </div>

              <div className="signup-heading">
                <span className="signup-top-label">CREATE ACCOUNT</span>

                <h2>Admin Registration</h2>

                <p>Get started by creating your administrator account.</p>
              </div>

              {serverError && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="signup-error-box"
                  role="alert"
                >
                  <i className="bi bi-exclamation-circle-fill" />
                  {serverError}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="signup-input-row">
                  <div className="signup-field">
                    <label htmlFor="fname">First Name</label>
                    <div
                      className={`signup-input-wrap ${errors.fname ? "has-error" : ""}`}
                    >
                      <i className="bi bi-person" />
                      <input
                        id="fname"
                        type="text"
                        name="fname"
                        value={formData.fname}
                        onChange={handleChange}
                        placeholder="Enter first name"
                        autoComplete="given-name"
                      />
                    </div>
                    {errors.fname && (
                      <small className="signup-field-error">
                        {errors.fname}
                      </small>
                    )}
                  </div>

                  <div className="signup-field">
                    <label htmlFor="lname">Last Name</label>
                    <div
                      className={`signup-input-wrap ${errors.lname ? "has-error" : ""}`}
                    >
                      <i className="bi bi-person" />
                      <input
                        id="lname"
                        type="text"
                        name="lname"
                        value={formData.lname}
                        onChange={handleChange}
                        placeholder="Enter last name"
                        autoComplete="family-name"
                      />
                    </div>
                    {errors.lname && (
                      <small className="signup-field-error">
                        {errors.lname}
                      </small>
                    )}
                  </div>
                </div>

                <div className="signup-field">
                  <label htmlFor="email">Email Address</label>
                  <div
                    className={`signup-input-wrap ${errors.email ? "has-error" : ""}`}
                  >
                    <i className="bi bi-envelope" />
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && (
                    <small className="signup-field-error">{errors.email}</small>
                  )}
                </div>

                <div className="signup-field">
                  <label htmlFor="mobile">Mobile Number</label>
                  <div
                    className={`signup-input-wrap ${errors.mobile ? "has-error" : ""}`}
                  >
                    <i className="bi bi-telephone" />
                    <input
                      id="mobile"
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Enter mobile number"
                      autoComplete="tel"
                    />
                  </div>
                  {errors.mobile && (
                    <small className="signup-field-error">
                      {errors.mobile}
                    </small>
                  )}
                </div>

                <div className="signup-field">
                  <label htmlFor="password">Password</label>
                  <div
                    className={`signup-input-wrap ${errors.password ? "has-error" : ""}`}
                  >
                    <i className="bi bi-lock" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="signup-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <i
                        className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                      />
                    </button>
                  </div>
                  {errors.password && (
                    <small className="signup-field-error">
                      {errors.password}
                    </small>
                  )}
                </div>

                <motion.button
                  whileHover={{ scale: 1.015 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="signup-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Admin Account
                      <i className="bi bi-arrow-right ms-2" />
                    </>
                  )}
                </motion.button>

                <div className="signup-login-text">
                  Already have an account?
                  <NavLink to="/admin">Sign In</NavLink>
                </div>
              </form>

              <div className="signup-security-note">
                <i className="bi bi-shield-lock-fill" />
                Your administrator access is protected.
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signin;
